import { EstimateSwapView, SwapOptions, REF_FI_SWAP_SIGNAL } from './swapTypes';
import {
  executeMultipleTransactions,
  near,
  ONE_YOCTO_NEAR,
  REF_FI_CONTRACT_ID,
  RefFiFunctionCallOptions,
  Transaction,
} from './near';
import {
  ftGetStorageBalance,
  native_usdc_has_upgraded,
  TokenMetadata,
} from './ft-contract';
import { getCurrentWallet } from '../utils/wallets-integration';
import { STORAGE_TO_REGISTER_WITH_MFT } from './creators/storage';
import {
  percentLess,
  scientificNotationToString,
  separateRoutes,
  toNonDivisibleNumber,
  toReadableNumber,
} from '../utils/numbers';
import {
  nearDepositTransaction,
  nearWithdrawTransaction,
  WRAP_NEAR_CONTRACT_ID,
} from './wrap-near';
import { registerAccountOnToken } from './creators/token';
import { getSelectedWalletId } from './walletKeys';
import { round } from './token';
import Big from 'big.js';
import { JsonRpcProvider } from 'near-api-js/lib/providers';
import { auroraSwapTransactions } from './aurora/aurora';
import { BigNumber } from 'bignumber.js';
import { getPoolEstimate } from './swapPoolEstimates';
import getConfigV2 from './configV2';

const { NO_REQUIRED_REGISTRATION_TOKEN_IDS } = getConfigV2();

export const swap = async ({
  tokenIn,
  tokenOut,
  swapsToDo,
  slippageTolerance,
  amountIn,
  swapMarket,
}: SwapOptions) => {
  if (swapsToDo) {
    await instantSwap({
      tokenIn,
      tokenOut,
      amountIn,
      swapsToDo,
      slippageTolerance,
      swapMarket,
    });
  }
};

const instantSwap = async ({
  tokenIn,
  tokenOut,
  amountIn,
  swapsToDo,
  slippageTolerance,
  swapMarket,
}: SwapOptions) => {
  if (swapMarket === 'ref') {
    localStorage.setItem(REF_FI_SWAP_SIGNAL, 'ref');
    return nearInstantSwap({
      tokenIn,
      tokenOut,
      amountIn,
      swapsToDo,
      slippageTolerance,
    });
  } else if (swapMarket === 'tri') {
    localStorage.setItem(REF_FI_SWAP_SIGNAL, 'tri');
    return crossInstantSwap({
      tokenIn,
      tokenOut,
      amountIn,
      swapsToDo,
      slippageTolerance,
    });
  }
};

export const swapFromServer = async ({
  tokenIn,
  tokenOut,
  amountIn,
  swapsToDoServer,
}: SwapOptions) => {
  const transactions: Transaction[] = [];
  const tokenOutActions: RefFiFunctionCallOptions[] = [];
  const { contract_out, routes } = swapsToDoServer;
  const registerToken = async (token: TokenMetadata) => {
    const tokenRegistered = await ftGetStorageBalance(token.id).catch(() => {
      throw new Error(`${token.id} doesn't exist.`);
    });

    if (tokenRegistered === null) {
      if (NO_REQUIRED_REGISTRATION_TOKEN_IDS.includes(token.id)) {
        const hasUpgraded = await native_usdc_has_upgraded(token.id);
        if (hasUpgraded) {
          tokenOutActions.push({
            methodName: 'storage_deposit',
            args: {
              registration_only: true,
              account_id: getCurrentWallet()?.wallet?.getAccountId(),
            },
            gas: '30000000000000',
            amount: STORAGE_TO_REGISTER_WITH_MFT,
          });
        } else {
          tokenOutActions.push({
            methodName: 'register_account',
            args: {
              account_id: getCurrentWallet()?.wallet?.getAccountId(),
            },
            gas: '10000000000000',
          });
        }
      } else {
        tokenOutActions.push({
          methodName: 'storage_deposit',
          args: {
            registration_only: true,
            account_id: getCurrentWallet()?.wallet?.getAccountId(),
          },
          gas: '30000000000000',
          amount: STORAGE_TO_REGISTER_WITH_MFT,
        });
      }
      transactions.push({
        receiverId: token.id,
        functionCalls: tokenOutActions,
      });
    }
  };

  if (tokenOut.id !== contract_out) {
    return window.location.reload();
  }

  //making sure all actions get included.
  await registerToken(tokenOut);
  const actionsList = [];
  routes.forEach((route) => {
    route.pools.forEach((pool) => {
      if (+pool.amount_in == 0) {
        delete pool.amount_in;
      }
      pool.pool_id = Number(pool.pool_id);
      actionsList.push(pool);
    });
  });
  transactions.push({
    receiverId: tokenIn.id,
    functionCalls: [
      {
        methodName: 'ft_transfer_call',
        args: {
          receiver_id: REF_FI_CONTRACT_ID,
          amount: toNonDivisibleNumber(tokenIn.decimals, amountIn),
          msg: JSON.stringify({
            force: 0,
            actions: actionsList,
            ...(tokenOut.symbol == 'NEAR' ? { skip_unwrap_near: false } : {}),
          }),
        },
        gas: '300000000000000',
        amount: ONE_YOCTO_NEAR,
      },
    ],
  });

  if (tokenIn.id === WRAP_NEAR_CONTRACT_ID && tokenIn?.symbol == 'NEAR') {
    transactions.unshift(nearDepositTransaction(amountIn));
  }
  if (tokenIn.id === WRAP_NEAR_CONTRACT_ID) {
    const registered = await ftGetStorageBalance(WRAP_NEAR_CONTRACT_ID);
    if (registered === null) {
      transactions.unshift({
        receiverId: WRAP_NEAR_CONTRACT_ID,
        functionCalls: [registerAccountOnToken()],
      });
    }
  }

  return executeMultipleTransactions(transactions);
};

export const swapFromFourPool = async ({
  tokenIn,
  tokenOut,
  amountIn, // NonDivisibleNumber
  pool_id,
  min_amount_out,
}) => {
  const transactions: Transaction[] = [];
  const tokenOutActions: RefFiFunctionCallOptions[] = [];
  const walletId = getSelectedWalletId();
  const gas = walletId == 'neth' ? '250000000000000' : '300000000000000';
  const registerToken = async (token: TokenMetadata) => {
    const tokenRegistered = await ftGetStorageBalance(token.id).catch(() => {
      throw new Error(`${token.id} doesn't exist.`);
    });
    if (tokenRegistered === null) {
      tokenOutActions.push({
        methodName: 'storage_deposit',
        args: {
          registration_only: true,
          account_id: getCurrentWallet()?.wallet?.getAccountId(),
        },
        gas,
        amount: STORAGE_TO_REGISTER_WITH_MFT,
      });
      transactions.push({
        receiverId: token.id,
        functionCalls: tokenOutActions,
      });
    }
  };
  await registerToken(tokenOut);
  const actionsList = [
    {
      pool_id: +pool_id,
      token_in: tokenIn.id,
      token_out: tokenOut.id,
      amount_in: amountIn,
      min_amount_out,
    },
  ];
  transactions.push({
    receiverId: tokenIn.id,
    functionCalls: [
      {
        methodName: 'ft_transfer_call',
        args: {
          receiver_id: REF_FI_CONTRACT_ID,
          amount: amountIn,
          msg: JSON.stringify({
            force: 0,
            actions: actionsList,
          }),
        },
        gas,
        amount: ONE_YOCTO_NEAR,
      },
    ],
  });

  return executeMultipleTransactions(transactions);
};

const nearInstantSwap = async ({
  tokenIn,
  tokenOut,
  amountIn,
  swapsToDo,
  slippageTolerance,
}: SwapOptions) => {
  const transactions: Transaction[] = [];
  const tokenOutActions: RefFiFunctionCallOptions[] = [];

  const registerToken = async (token: TokenMetadata) => {
    const tokenRegistered = await ftGetStorageBalance(token.id).catch(() => {
      throw new Error(`${token.id} doesn't exist.`);
    });

    if (tokenRegistered === null) {
      if (NO_REQUIRED_REGISTRATION_TOKEN_IDS.includes(token.id)) {
        const hasUpgraded = await native_usdc_has_upgraded(token.id);
        if (hasUpgraded) {
          tokenOutActions.push({
            methodName: 'storage_deposit',
            args: {
              registration_only: true,
              account_id: getCurrentWallet()?.wallet?.getAccountId(),
            },
            gas: '30000000000000',
            amount: STORAGE_TO_REGISTER_WITH_MFT,
          });
        } else {
          tokenOutActions.push({
            methodName: 'register_account',
            args: {
              account_id: getCurrentWallet()?.wallet?.getAccountId(),
            },
            gas: '10000000000000',
          });
        }
      } else {
        tokenOutActions.push({
          methodName: 'storage_deposit',
          args: {
            registration_only: true,
            account_id: getCurrentWallet()?.wallet?.getAccountId(),
          },
          gas: '30000000000000',
          amount: STORAGE_TO_REGISTER_WITH_MFT,
        });
      }
      transactions.push({
        receiverId: token.id,
        functionCalls: tokenOutActions,
      });
    }
  };

  if (tokenOut.id !== swapsToDo[swapsToDo.length - 1].outputToken) {
    return window.location.reload();
  }

  //making sure all actions get included.
  await registerToken(tokenOut);
  const actionsList = [];
  const allSwapsTokens = swapsToDo.map((swapTodo) => [
    swapTodo.inputToken,
    swapTodo.outputToken,
  ]); // to get the hop tokens

  for (const swapIndex in allSwapsTokens) {
    const swapTokens = allSwapsTokens[swapIndex];
    if (swapTokens[0] == tokenIn.id && swapTokens[1] == tokenOut.id) {
      // direct pool
      actionsList.push({
        pool_id: swapsToDo[swapIndex].pool.id,
        token_in: tokenIn.id,
        token_out: tokenOut.id,
        amount_in: swapsToDo[swapIndex].partialAmountIn,
        min_amount_out: round(
          tokenOut.decimals,
          toNonDivisibleNumber(
            tokenOut.decimals,
            percentLess(slippageTolerance, swapsToDo[swapIndex].estimate)
          )
        ),
      });
    } else if (swapTokens[1] == tokenOut.id) {
      // other hops
      actionsList.push({
        pool_id: swapsToDo[swapIndex].pool.id,
        token_in: swapTokens[0],
        token_out: swapTokens[1],
        min_amount_out: round(
          tokenOut.decimals,
          toNonDivisibleNumber(
            tokenOut.decimals,
            percentLess(slippageTolerance, swapsToDo[swapIndex].estimate)
          )
        ),
      });
    } else if (swapTokens[0] === tokenIn.id) {
      // first hop
      actionsList.push({
        pool_id: swapsToDo[swapIndex].pool.id,
        token_in: swapTokens[0],
        token_out: swapTokens[1],
        amount_in: swapsToDo[swapIndex].partialAmountIn,
        min_amount_out: '0',
      });
    } else {
      // middle hop
      actionsList.push({
        pool_id: swapsToDo[swapIndex].pool.id,
        token_in: swapTokens[0],
        token_out: swapTokens[1],
        min_amount_out: '0',
      });
    }
  }
  const gas =
    getSelectedWalletId() == 'neth' ? '2500000000000000' : '300000000000000';
  transactions.push({
    receiverId: tokenIn.id,
    functionCalls: [
      {
        methodName: 'ft_transfer_call',
        args: {
          receiver_id: REF_FI_CONTRACT_ID,
          amount: toNonDivisibleNumber(tokenIn.decimals, amountIn),
          msg: JSON.stringify({
            force: 0,
            actions: actionsList,
            ...(tokenOut.symbol == 'NEAR' ? { skip_unwrap_near: false } : {}),
          }),
        },
        gas,
        amount: ONE_YOCTO_NEAR,
      },
    ],
  });

  if (tokenIn.id === WRAP_NEAR_CONTRACT_ID && tokenIn?.symbol == 'NEAR') {
    transactions.unshift(nearDepositTransaction(amountIn));
  }
  if (tokenIn.id === WRAP_NEAR_CONTRACT_ID) {
    const registered = await ftGetStorageBalance(WRAP_NEAR_CONTRACT_ID);
    if (registered === null) {
      transactions.unshift({
        receiverId: WRAP_NEAR_CONTRACT_ID,
        functionCalls: [registerAccountOnToken()],
      });
    }
  }

  return executeMultipleTransactions(transactions);
};

const crossInstantSwap = async ({
  tokenIn,
  tokenOut,
  amountIn,
  swapsToDo,
  slippageTolerance,
}: SwapOptions) => {
  const transactions: Transaction[] = [];

  const { wallet } = getCurrentWallet();

  // to register actiosn

  const registerToken = async (tokenId: string) => {
    const tokenRegistered = await ftGetStorageBalance(tokenId).catch(() => {
      throw new Error(`${tokenId} doesn't exist.`);
    });
    const tokenOutActions: RefFiFunctionCallOptions[] = [];
    if (tokenRegistered === null) {
      tokenOutActions.push({
        methodName: 'storage_deposit',
        args: {
          registration_only: true,
          account_id: getCurrentWallet()?.wallet?.getAccountId(),
        },
        gas: '30000000000000',
        amount: STORAGE_TO_REGISTER_WITH_MFT,
      });

      transactions.push({
        receiverId: tokenId,
        functionCalls: tokenOutActions,
      });
    }
  };

  // await registerToken(tokenOut.id);

  const routes = separateRoutes(swapsToDo, tokenOut.id);

  const forceRegisterTokens: string[] = [];

  for (let routeIndex = 0; routeIndex < routes.length; routeIndex++) {
    const curRoute = routes[routeIndex];

    if (curRoute[curRoute.length - 1].pool.Dex === 'tri') {
      forceRegisterTokens.push(
        curRoute[0].tokens[curRoute[0].tokens.length - 1].id
      );
    } else if (
      curRoute.length === 2 &&
      curRoute[0].pool.Dex === 'tri' &&
      curRoute[1].pool.Dex !== 'tri'
    ) {
      forceRegisterTokens.push(curRoute[0].tokens[1].id);
    }
  }

  // force register
  new Array(...new Set(forceRegisterTokens)).map(
    (id) =>
      id !== 'usn' &&
      transactions.push({
        receiverId: id,
        functionCalls: [registerAccountOnToken()],
      })
  );

  const validateRegisterTokens: string[] = [];

  for (let routeIndex = 0; routeIndex < routes.length; routeIndex++) {
    const curRoute = routes[routeIndex];

    if (curRoute[curRoute.length - 1].pool.Dex === 'ref') {
      validateRegisterTokens.push(
        curRoute[0].tokens[curRoute[0].tokens.length - 1].id
      );
    } else if (
      curRoute.length === 2 &&
      curRoute[0].pool.Dex !== 'tri' &&
      curRoute[1].pool.Dex === 'tri'
    ) {
      validateRegisterTokens.push(curRoute[0].tokens[1].id);
    }
  }

  await Promise.all(
    new Array(...new Set(validateRegisterTokens))
      .filter((id) => !forceRegisterTokens.includes(id) && id !== 'usn')
      .map((token) => registerToken(token))
  );

  if (wallet.isSignedIn()) {
    const routes = separateRoutes(swapsToDo, tokenOut.id);

    for (let routeIndex = 0; routeIndex < routes.length; routeIndex++) {
      const todosThisRoute = routes[routeIndex];

      if (todosThisRoute.length === 1) {
        const curTransactions = await parallelSwapCase({
          tokenIn,
          tokenOut,
          amountIn,
          swapsToDo: todosThisRoute,
          slippageTolerance,
        });

        curTransactions.forEach((transaction) =>
          transactions.push(transaction)
        );
      } else {
        const curTransactions = await smartRouteSwapCase({
          tokenIn,
          tokenOut,
          amountIn:
            routes.length === 1
              ? amountIn
              : toReadableNumber(
                  tokenIn.decimals,
                  todosThisRoute[0].pool.partialAmountIn
                ),
          swapsToDo: todosThisRoute,
          slippageTolerance,
        });
        curTransactions.forEach((transaction) =>
          transactions.push(transaction)
        );
      }
    }
    if (tokenIn.id === WRAP_NEAR_CONTRACT_ID && tokenIn.symbol == 'NEAR') {
      transactions.unshift(nearDepositTransaction(amountIn));
    }
    if (tokenOut.id === WRAP_NEAR_CONTRACT_ID) {
      const outEstimate = new Big(0);
      const routes = separateRoutes(swapsToDo, tokenOut.id);

      const bigEstimate = routes.reduce((total, route) => {
        const curEstimate = route[route.length - 1].estimate;
        return total.plus(curEstimate);
      }, outEstimate);

      const minAmountOut = percentLess(
        slippageTolerance,

        scientificNotationToString(bigEstimate.toString())
      );
      if (tokenOut.symbol == 'NEAR') {
        transactions.push(nearWithdrawTransaction(minAmountOut));
      }
    }
    if (tokenIn.id === WRAP_NEAR_CONTRACT_ID) {
      const registered = await ftGetStorageBalance(WRAP_NEAR_CONTRACT_ID);
      if (registered === null) {
        transactions.unshift({
          receiverId: WRAP_NEAR_CONTRACT_ID,
          functionCalls: [registerAccountOnToken()],
        });
      }
    }

    return executeMultipleTransactions(transactions);
  }
};

export const checkTransaction = (txHash: string) => {
  return (near.connection.provider as JsonRpcProvider).sendJsonRpc(
    'EXPERIMENTAL_tx_status',
    [txHash, getCurrentWallet()?.wallet?.getAccountId()]
  );
};

export const checkTransactionStatus = (txHash: string) => {
  return near.connection.provider.txStatus(
    txHash,
    getCurrentWallet()?.wallet?.getAccountId()
  );
};

const parallelSwapCase = async ({
  tokenIn,
  tokenOut,
  amountIn,
  swapsToDo,
  slippageTolerance,
}: {
  tokenIn: TokenMetadata;
  tokenOut: TokenMetadata;
  amountIn: string;
  swapsToDo: EstimateSwapView[];
  slippageTolerance: number;
}) => {
  const curTransactions: Transaction[] = [];

  // separate todos to different dexes

  const refSwapTodos = swapsToDo.filter(
    (swapTodo) => swapTodo.pool.Dex === 'ref'
  );

  const triSwapTodos = swapsToDo.filter(
    (swapTodo) => swapTodo.pool.Dex === 'tri'
  );

  if (triSwapTodos.length > 0) {
    const triSwapTransactions = await auroraSwapTransactions({
      tokenIn_id: tokenIn.id,
      tokenOut_id: tokenOut.id,
      swapTodos: triSwapTodos,
      readableAmountIn: amountIn,
      decimalIn: tokenIn.decimals,
      decimalOut: tokenOut.decimals,
      slippageTolerance,
      swapType: 'parallel',
    });

    triSwapTransactions.forEach((transaction) =>
      curTransactions.push(transaction)
    );
  }

  if (refSwapTodos.length > 0) {
    const refSwapActions = refSwapTodos.map((s2d) => {
      const minTokenOutAmount = s2d.estimate
        ? percentLess(slippageTolerance, s2d.estimate)
        : '0';
      const allocation = toReadableNumber(
        tokenIn.decimals,
        scientificNotationToString(s2d.pool.partialAmountIn)
      );

      return {
        pool_id: s2d.pool.id,
        token_in: tokenIn.id,
        token_out: tokenOut.id,
        amount_in: round(
          tokenIn.decimals,
          toNonDivisibleNumber(tokenIn.decimals, allocation)
        ),
        min_amount_out: round(
          tokenOut.decimals,
          toNonDivisibleNumber(tokenOut.decimals, minTokenOutAmount)
        ),
      };
    });

    const nearTransactionAmount = scientificNotationToString(
      BigNumber.sum(
        ...refSwapTodos.map((todo) => todo.pool.partialAmountIn)
      ).toString()
    );

    curTransactions.push({
      receiverId: tokenIn.id,
      functionCalls: [
        {
          methodName: 'ft_transfer_call',
          args: {
            receiver_id: REF_FI_CONTRACT_ID,
            amount: nearTransactionAmount,
            msg: JSON.stringify({
              force: 0,
              actions: refSwapActions,
            }),
          },
          gas: '180000000000000',
          amount: ONE_YOCTO_NEAR,
        },
      ],
    });
  }

  // could be hybrid transactions on difference dexes
  return curTransactions;
};

const smartRouteSwapCase = async ({
  tokenIn,
  tokenOut,
  amountIn,
  swapsToDo,
  slippageTolerance,
}: {
  tokenIn: TokenMetadata;
  tokenOut: TokenMetadata;
  amountIn: string;
  swapsToDo: EstimateSwapView[];
  slippageTolerance: number;
}) => {
  const curTransactions: Transaction[] = [];
  const actionsList = []; // for ref swap actions

  const amountInInt = toNonDivisibleNumber(tokenIn.decimals, amountIn);

  const firstSwap = swapsToDo[0];
  const secondSwap = swapsToDo[1];
  const swap1toTri = firstSwap.pool.Dex === 'tri';

  const swap2toTri = secondSwap.pool.Dex === 'tri';
  let triSwapTransactions: Transaction[] = [];

  if (swap1toTri && swap2toTri) {
    triSwapTransactions = await auroraSwapTransactions({
      tokenIn_id: firstSwap.inputToken,
      tokenOut_id: secondSwap.outputToken,
      swapTodos: swapsToDo,
      readableAmountIn: amountIn,
      decimalIn: tokenIn.decimals,
      decimalOut: tokenOut.decimals,
      slippageTolerance,
      swapType: 'smartV1',
      readableAmountOut: percentLess(
        slippageTolerance,
        swapsToDo[swapsToDo.length - 1].estimate
      ),
    });
    triSwapTransactions.forEach((transaction) =>
      curTransactions.push(transaction)
    );
  } else if (swap1toTri) {
    // first pool on tri
    const secondAmountIn = round(
      secondSwap.tokens[1].decimals,
      toNonDivisibleNumber(
        secondSwap.tokens[1].decimals,
        percentLess(slippageTolerance, firstSwap.estimate)
      )
    );

    const secondHopEstimateOut = await getPoolEstimate({
      tokenIn: secondSwap.tokens[1],
      tokenOut,
      amountIn: secondAmountIn,
      Pool: secondSwap.pool,
    });

    triSwapTransactions = await auroraSwapTransactions({
      tokenIn_id: tokenIn.id,
      tokenOut_id: firstSwap.outputToken,
      swapTodos: [firstSwap],
      readableAmountIn: amountIn,
      decimalIn: tokenIn.decimals,
      decimalOut: firstSwap.tokens[1].decimals,
      slippageTolerance,
      swapType: 'smartV1',
      readableAmountOut: toReadableNumber(
        secondSwap.tokens[1].decimals,
        secondAmountIn
      ),
    });
    triSwapTransactions.forEach((transaction) =>
      curTransactions.push(transaction)
    );

    // slippage tolerance from first action
    actionsList.push({
      pool_id: secondSwap.pool.id,
      token_in: secondSwap.inputToken,
      token_out: secondSwap.outputToken,
      amount_in: secondAmountIn,
      min_amount_out: round(
        tokenOut.decimals,
        toNonDivisibleNumber(
          tokenOut.decimals,
          percentLess(slippageTolerance, secondHopEstimateOut.estimate)
        )
      ),
    });

    curTransactions.push({
      receiverId: secondSwap.inputToken,
      functionCalls: [
        {
          methodName: 'ft_transfer_call',
          args: {
            receiver_id: REF_FI_CONTRACT_ID,
            amount: secondAmountIn,
            msg: JSON.stringify({
              force: 0,
              actions: actionsList,
            }),
          },
          gas: '300000000000000',
          amount: ONE_YOCTO_NEAR,
        },
      ],
    });
  } else if (swap2toTri) {
    // second pool on tri

    const secondAmountIn = round(
      firstSwap.tokens[1].decimals,
      toNonDivisibleNumber(
        firstSwap.tokens[1].decimals,
        percentLess(slippageTolerance, firstSwap.estimate)
      )
    );

    const secondHopEstimateOut = await getPoolEstimate({
      tokenIn: secondSwap.tokens[1],
      tokenOut,
      amountIn: secondAmountIn,
      Pool: secondSwap.pool,
    });

    actionsList.push({
      pool_id: firstSwap.pool.id,
      token_in: firstSwap.inputToken,
      token_out: firstSwap.outputToken,
      amount_in: amountInInt,
      min_amount_out: secondAmountIn,
    });

    curTransactions.push({
      receiverId: tokenIn.id,
      functionCalls: [
        {
          methodName: 'ft_transfer_call',
          args: {
            receiver_id: REF_FI_CONTRACT_ID,
            amount: amountInInt,
            msg: JSON.stringify({
              force: 0,
              actions: actionsList,
            }),
          },
          gas: '300000000000000',
          amount: ONE_YOCTO_NEAR,
        },
      ],
    });

    triSwapTransactions = await auroraSwapTransactions({
      tokenIn_id: secondSwap.inputToken,
      tokenOut_id: secondSwap.outputToken,
      swapTodos: [secondSwap],
      readableAmountIn: toReadableNumber(
        firstSwap.tokens[1].decimals,
        secondAmountIn
      ),
      decimalIn: secondSwap.tokens[1].decimals,
      decimalOut: tokenOut.decimals,
      slippageTolerance,
      swapType: 'smartV1',
      readableAmountOut: toReadableNumber(
        tokenOut.decimals,
        round(
          tokenOut.decimals,
          toNonDivisibleNumber(
            tokenOut.decimals,
            percentLess(slippageTolerance, secondHopEstimateOut.estimate)
          )
        )
      ),
    });
    triSwapTransactions.forEach((transaction) =>
      curTransactions.push(transaction)
    );
  } else {
    // no pool on tri
    actionsList.push({
      pool_id: firstSwap.pool.id,
      token_in: firstSwap.inputToken,
      token_out: firstSwap.outputToken,
      amount_in: amountInInt,
      min_amount_out: '0',
    });
    actionsList.push({
      pool_id: secondSwap.pool.id,
      token_in: secondSwap.inputToken,
      token_out: secondSwap.outputToken,
      min_amount_out: round(
        tokenOut.decimals,
        toNonDivisibleNumber(
          tokenOut.decimals,
          percentLess(slippageTolerance, swapsToDo[1].estimate)
        )
      ),
    });
    curTransactions.push({
      receiverId: tokenIn.id,
      functionCalls: [
        {
          methodName: 'ft_transfer_call',
          args: {
            receiver_id: REF_FI_CONTRACT_ID,
            amount: toNonDivisibleNumber(tokenIn.decimals, amountIn),
            msg: JSON.stringify({
              force: 0,
              actions: actionsList,
            }),
          },
          gas: '300000000000000',
          amount: ONE_YOCTO_NEAR,
        },
      ],
    });
  }

  // separate todos to different dexes

  return curTransactions;
};
